import * as ordersApi from "@/apiv2/planner/orders";
import { createAction, createSetterActions } from "@/redux/helpers/action";
import { handleError } from "@/redux/utils/error";

import { DRAWER_MODE, MODULE } from "./constants";
import * as selectors from "./drawer.selectors";

// Simple setter actions (dispatch with value)
// `blockDraftIncomplete` (F08): EntryModeBody is mid-way through marking an entry blocked with
// reason "Other" but no note yet, so nothing has been saved. PlannerDrawer reads it to guard every
// dismiss path and show the "Add a reason or discard?" modal instead of closing.
// `pendingBlock` (F08): the reason picker is open but nothing is blocked yet. It lives here rather
// than inside EntryModeBody because the right-click "Mark blocked" opens the drawer straight into
// this state, so both entry points share it.
export const [
  setIsOpen,
  setMode,
  setActiveEntry,
  setActiveOrder,
  setActiveEvent,
  setBlockDraftIncomplete,
  setPendingBlock,
  setOrderDetails,
  setIsFetchingOrderDetails,
] = createSetterActions(MODULE, [
  "isOpen",
  "mode",
  "activeEntry",
  "activeOrder",
  "activeEvent",
  "blockDraftIncomplete",
  "pendingBlock",
  "orderDetails",
  "isFetchingOrderDetails",
]);

export const reset = createAction(`${MODULE}/RESET`);

// The drawer can be reopened on a different order while this is in flight, so a late reply must
// not overwrite the newer one.
export const fetchOrderDetails = (uid) => async (dispatch, getState) => {
  dispatch(setIsFetchingOrderDetails(true));

  try {
    const { data } = await ordersApi.getOrderDetails(uid)();

    if (selectors.getActiveOrder(getState())?.uid === uid) {
      dispatch(setOrderDetails(data));
    }
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingOrderDetails(false));
  }
};

// Open the drawer in entry mode for a scheduled board entry (event.original). Pass
// `{ pendingBlock: true }` to land straight in the F08 reason picker (the right-click "Mark
// blocked" path) - nothing is written until a reason is picked, exactly like the drawer's own
// Mark blocked button.
export const openEntryDrawer =
  (entry, { pendingBlock = false } = {}) =>
  (dispatch) => {
    dispatch(setActiveEntry(entry));
    dispatch(setActiveOrder(null));
    dispatch(setBlockDraftIncomplete(false));
    dispatch(setPendingBlock(pendingBlock));
    dispatch(setMode(DRAWER_MODE.ENTRY));
    dispatch(setIsOpen(true));
  };

// Open the drawer in order mode from an Orders-column card click.
export const openOrderDrawer = (order) => (dispatch) => {
  dispatch(setActiveOrder(order));
  dispatch(setActiveEntry(null));
  dispatch(setMode(DRAWER_MODE.ORDER));
  dispatch(setIsOpen(true));
};

// Open the drawer in event mode for a custom calendar event (event.meta). Events are their own
// surface: clicking one must never open the order drawer.
export const openEventDrawer = (event) => (dispatch) => {
  dispatch(setActiveEvent(event));
  dispatch(setActiveEntry(null));
  dispatch(setActiveOrder(null));
  dispatch(setMode(DRAWER_MODE.EVENT));
  dispatch(setIsOpen(true));
};

// Closing IS the rollback for an in-progress block: nothing is written until a reason is valid, and
// EntryModeBody unmounts with the drawer, so its pending state simply goes away.
export const closeDrawer = () => (dispatch) => {
  dispatch(setBlockDraftIncomplete(false));
  dispatch(setPendingBlock(false));
  dispatch(setIsOpen(false));
};
